import { useMemo, useState } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import useLoyaltyAccount from 'loyalty/hooks/useLoyaltyAccount'
import getLoyaltyTierThresholds, { getTierThreshold } from 'loyalty/selectors/getLoyaltyTierThresholds'
import { clamp } from 'lib/maths/mathUtils'
import { getTierProgressionRetainModeState } from 'loyalty/lib/tierProgressionRetainModeState'

interface TierProgressionMode {
  unlockCardTier: App.LoyaltyTier
  effectiveRetainMode: boolean
  canToggleView: boolean
  showUpgradeView: boolean
  setShowUpgradeView: React.Dispatch<React.SetStateAction<boolean>>
  targetCredits: number
  targetNights: number
  creditsRemaining: number
  nightsRemaining: number
  hasRetainedTier: boolean
  statusCreditsProgress: number
  nightsProgress: number
}

/**
 * Determines whether the current member is in "retain tier" or "unlock next tier" mode,
 * and computes all derived progression values (targets, remaining, progress percentages).
 *
 * Retain mode activates when a Silver/Gold/Platinum member's status credits or nights
 * fall below the threshold needed to maintain their current tier, or when the member
 * is Platinum (no next tier). At-risk Silver/Gold members can toggle between
 * retain and upgrade views.
 */
function useLoyaltyTierProgressionMode(): TierProgressionMode {
  const account = useLoyaltyAccount().data!
  const [tier, _tierName, nextTier, _nextTierName] = useCurrentLoyaltyTier()

  // The API nullifies zero thresholds (nullifyIfZero), which the frontend mapper converts back to 0.
  // Fall back to tier thresholds config when the account values are 0.
  const tierThresholds = useAppSelector(getLoyaltyTierThresholds)
  const currentTierThreshold = tierThresholds ? getTierThreshold(tierThresholds, tier) : undefined
  const nextTierThreshold = tierThresholds && nextTier ? getTierThreshold(tierThresholds, nextTier) : undefined
  const retentionCreditsThreshold = account.totalCreditsForTierRetention || currentTierThreshold?.statusCredits || 0
  const retentionNightsThreshold = account.numberOfNightsForTierRetention || currentTierThreshold?.nights || 0
  const upgradeCreditsThreshold = account.totalCreditsForTierUpgrade || nextTierThreshold?.statusCredits || 0
  const upgradeNightsThreshold = account.numberOfNightsForTierUpgrade || nextTierThreshold?.nights || 0

  const { isRetainMode, isAtRiskOfDowngrade } = getTierProgressionRetainModeState(tier, nextTier, account, tierThresholds)
  const canToggleView = isAtRiskOfDowngrade && !!nextTier
  const [showUpgradeView, setShowUpgradeView] = useState(false)
  const effectiveRetainMode = isRetainMode && !showUpgradeView

  const unlockCardTier = effectiveRetainMode ? tier : (nextTier ?? tier)

  // Target values based on mode (retain vs upgrade)
  const targetCredits = effectiveRetainMode ? retentionCreditsThreshold : upgradeCreditsThreshold
  const targetNights = effectiveRetainMode ? retentionNightsThreshold : upgradeNightsThreshold

  const creditsRemaining = Math.max(targetCredits - account.statusCredits, 0)
  const nightsRemaining = Math.max(targetNights - account.numberOfNights, 0)

  const hasRetainedTier = effectiveRetainMode && creditsRemaining === 0 && nightsRemaining === 0

  const statusCreditsProgress = targetCredits === 0 ? 100 : clamp(0, Math.floor((account.statusCredits / targetCredits) * 100), 100)
  const nightsProgress = targetNights === 0 ? 100 : clamp(0, Math.floor((account.numberOfNights / targetNights) * 100), 100)

  return useMemo((): TierProgressionMode => ({
    unlockCardTier,
    effectiveRetainMode,
    canToggleView,
    showUpgradeView,
    setShowUpgradeView,
    targetCredits,
    targetNights,
    creditsRemaining,
    nightsRemaining,
    hasRetainedTier,
    statusCreditsProgress,
    nightsProgress,
  }), [
    unlockCardTier,
    effectiveRetainMode,
    canToggleView,
    showUpgradeView,
    setShowUpgradeView,
    targetCredits,
    targetNights,
    creditsRemaining,
    nightsRemaining,
    hasRetainedTier,
    statusCreditsProgress,
    nightsProgress,
  ])
}

export default useLoyaltyTierProgressionMode
